import { Component, inject, signal, computed, effect, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { UsersService } from '../users.service';
import { User } from '../user.model';
import { UserItemComponent } from '../user-item/user-item.component';
import { AddButtonComponent } from '../../../shared/components/add-button/add-button.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [FormsModule, UserItemComponent, AddButtonComponent],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.css'
})
export class UserListComponent implements OnDestroy {
  private readonly usersService = inject(UsersService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  searchTerm = '';
  debouncedSearchTerm = signal('');
  loading = signal(true);

  // Determine if we're on operators or users route
  isOperatorsRoute = toSignal(
    this.route.url.pipe(map(segments => segments[0]?.path === 'operators')),
    { initialValue: false }
  );

  // Page title based on route
  pageTitle = computed(() => this.isOperatorsRoute() ? 'Operators' : 'Users');

  // Fetch users based on route
  private users$ = this.route.url.pipe(
    switchMap(segments => {
      const isOperators = segments[0]?.path === 'operators';
      const roles = isOperators ? ['admin', 'operator'] : ['user'];
      return this.usersService.getUsersByRoles(roles);
    })
  );

  users = toSignal(this.users$, { initialValue: [] as User[] });

  filteredUsers = computed(() => {
    const term = this.debouncedSearchTerm().trim().toLowerCase();
    const userList = this.users();

    if (term.length < 3) return userList;
    
    return userList.filter(user =>
      user.displayName?.toLowerCase().includes(term) ||
      user.phoneNumber?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });

  constructor() {
    // Update loading state when users arrive
    effect(() => {
      const userList = this.users();
      if (userList.length >= 0) {
        this.loading.set(false);
      }
    });

    // Debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(term => this.debouncedSearchTerm.set(term));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(term: string) {
    this.searchSubject.next(term);
  }

  onAddOperator() {
    this.router.navigate(['/main/operators/new']);
  }
}
